import React, { useState, useEffect } from 'react';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';

// Market configuration
const MARKET_CONFIG = {
  india: {
    endpoint: 'india-stocks-health',
    title: 'India Indexes',
    icon: 'IN',
    iconColors: 'from-orange-500 to-yellow-500',
    navigateMarket: 'india',
    gridCols: 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3',
  },
  us: {
    endpoint: 'us-stocks-health',
    title: 'US Indexes',
    icon: 'US',
    iconColors: 'from-blue-500 to-purple-600',
    navigateMarket: 'us',
    gridCols: 'grid-cols-1 lg:grid-cols-2',
  },
  asia_pacific: {
    endpoint: 'asia-pacific-health',
    title: 'Asia-Pacific Indexes',
    icon: 'AP',
    iconColors: 'from-red-500 to-pink-600',
    navigateMarket: 'international',
    gridCols: 'grid-cols-1 lg:grid-cols-2',
  },
  europe: {
    endpoint: 'europe-health',
    title: 'Europe Indexes',
    icon: 'EU',
    iconColors: 'from-indigo-500 to-blue-600',
    navigateMarket: 'international',
    gridCols: 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3',
  },
};

// Symbol-specific icon labels
const SYMBOL_ICONS = {
  SENSEX: 'SX', NIFTY50: 'N50', NIFTYBANK: 'BNK', NIFTYIT: 'IT',
  NIFTYMIDCAP100: 'MID', NIFTYSMALLCAP100: 'SML', NIFTYPHARMA: 'PHR',
  NIFTYENERGY: 'ENR', NIFTYAUTO: 'AUT',
  SNP: 'S&P', IXIC: 'NDQ', DJI: 'DOW', RUT: 'RUT',
  HSI: 'HSI', N225: 'N225',
  DAX: 'DAX', FTSE: 'FTSE', CAC40: 'CAC', IMOEX: 'MOX',
};

// Symbol-specific gradient colors
const SYMBOL_COLORS = {
  SENSEX: 'from-blue-500 to-purple-600', NIFTY50: 'from-blue-400 to-indigo-600',
  NIFTYBANK: 'from-green-500 to-teal-600', NIFTYIT: 'from-purple-500 to-pink-600',
  NIFTYMIDCAP100: 'from-cyan-500 to-blue-600', NIFTYSMALLCAP100: 'from-amber-500 to-orange-600',
  SNP: 'from-blue-500 to-purple-600', IXIC: 'from-green-500 to-emerald-600',
  DJI: 'from-indigo-500 to-blue-600', RUT: 'from-violet-500 to-purple-600',
  HSI: 'from-red-500 to-rose-600', N225: 'from-pink-500 to-red-600',
  DAX: 'from-yellow-500 to-amber-600', FTSE: 'from-sky-500 to-blue-600',
  CAC40: 'from-indigo-500 to-violet-600',
  IMOEX: 'from-red-500 to-amber-600',
};

const StockHealthMonitor = ({ market, loading = false }) => {
  const [stocksHealth, setStocksHealth] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [isLoading, setIsLoading] = useState(loading);
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });
  const navigate = useNavigate();

  const config = MARKET_CONFIG[market];

  const periods = [
    { days: 7, label: '7D' },
    { days: 14, label: '14D' },
    { days: 30, label: '30D' },
  ];

  useEffect(() => {
    if (!config) return;
    const fetchStocksHealth = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_BASE_URL}/analytics/${config.endpoint}/?days=${selectedPeriod}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setStocksHealth(data);
      } catch (error) {
        console.error(`Error fetching ${market} health:`, error);
        setStocksHealth({ error: error.message });
      } finally {
        setIsLoading(false);
      }
    };
    fetchStocksHealth();
  }, [selectedPeriod, market, config]);

  if (!config) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'critical': return 'bg-red-500';
      case 'missing': return 'bg-red-700';
      case 'close_mismatch': return 'bg-orange-500';
      case 'holiday': return 'bg-blue-500/60';
      case 'pending': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const handleSymbolNavigate = (symbol) => {
    navigate(`/indexes?market=${config.navigateMarket}&symbol=${symbol}&errors=errors_only`);
  };

  if (isLoading) {
    return (
      <div className="dashboard-card">
        <h3 className="text-xl font-semibold text-white mb-4">{config.title} (Loading...)</h3>
        <div className={`grid ${config.gridCols} gap-6`}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-dark-700 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (stocksHealth.error) {
    return (
      <div className="dashboard-card">
        <h3 className="text-xl font-semibold text-white mb-4">{config.title} - ERROR</h3>
        <div className="text-red-400"><p>Error: {stocksHealth.error}</p></div>
      </div>
    );
  }

  const stocks = stocksHealth.stocks_health || {};
  const stockCount = Object.keys(stocks).length;

  if (stockCount === 0) {
    return (
      <div className="dashboard-card">
        <h3 className="text-xl font-semibold text-white mb-4">{config.title} - No Data</h3>
        <div className="text-dark-400"><p>No index data found for this market</p></div>
      </div>
    );
  }

  return (
    <div className="dashboard-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${config.iconColors} flex items-center justify-center text-white text-[10px] font-bold`}>
            {config.icon}
          </div>
          <h3 className="text-xl font-semibold text-white">{config.title} ({stockCount})</h3>
        </div>
        <div className="flex space-x-1">
          {periods.map((period) => (
            <button
              key={period.days}
              onClick={() => setSelectedPeriod(period.days)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                selectedPeriod === period.days ? 'tab-active' : 'tab-inactive'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4 text-[10px] text-dark-400">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-green-500 inline-block"></span>Healthy</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-yellow-500 inline-block"></span>Warning</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500 inline-block"></span>Critical</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-700 inline-block"></span>Missing</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-orange-500 inline-block"></span>Close Mismatch</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-500/60 inline-block"></span>Holiday</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-gray-500 inline-block"></span>Pending</span>
      </div>

      {/* Cards Grid */}
      <div className={`grid ${config.gridCols} gap-6`}>
        {Object.entries(stocks).map(([symbol, data]) => {
          const hasCriticalIssues = data.overall_health === 'critical' || data.critical_days > 0 || data.missing_days > 0;
          const hasCloseMismatch = data.close_mismatch_days > 0;
          const iconLabel = SYMBOL_ICONS[symbol] || symbol.substring(0, 3);
          const iconColor = SYMBOL_COLORS[symbol] || config.iconColors;

          return (
            <div key={symbol} className={`border rounded-lg p-5 bg-gradient-to-br from-dark-800/50 to-dark-900/50 transition-all hover:from-dark-700/50 hover:to-dark-800/50 ${
              hasCriticalIssues ? 'border-red-500/50 shadow-lg shadow-red-500/10' : hasCloseMismatch ? 'border-orange-500/50' : 'border-dark-700'
            }`}>

              {/* Critical Issues Alert */}
              {hasCriticalIssues && (
                <div className="mb-4 p-3 bg-red-900/30 border border-red-800/50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <span className="text-red-400 font-semibold text-sm">DATA ISSUES DETECTED</span>
                  </div>
                  <p className="text-red-300 text-xs mt-1">
                    {data.critical_days > 0 && `${data.critical_days} critical, `}
                    {data.missing_days > 0 && `${data.missing_days} missing`}
                    {hasCloseMismatch && `, ${data.close_mismatch_days} close mismatch`}
                  </p>
                </div>
              )}

              {/* Card Header - Clickable */}
              <div
                className="flex items-center justify-between mb-4 cursor-pointer hover:bg-dark-700/30 rounded-lg p-2 -m-2 transition-colors"
                onClick={() => handleSymbolNavigate(symbol)}
                title={`View detailed ${symbol} data quality records`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${iconColor} flex items-center justify-center font-bold text-white text-xs transition-transform hover:scale-105 ${
                    hasCriticalIssues ? 'ring-2 ring-red-500/50' : ''
                  }`}>
                    {iconLabel}
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white hover:text-primary-400 transition-colors">{data.symbol_name}</h4>
                    <p className="text-dark-400 text-sm">
                      {data.healthy_days}/{data.expected_records || data.days_analyzed} verified days
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 rounded text-xs ${
                    data.overall_health === 'healthy' ? 'bg-green-400/10 text-green-400' :
                    data.overall_health === 'warning' ? 'bg-yellow-400/10 text-yellow-400' :
                    'bg-red-400/10 text-red-400'
                  }`}>
                    {data.overall_health}
                  </span>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-4 gap-1.5 mb-4">
                <div className="text-center bg-dark-800 rounded-lg p-2">
                  <p className="text-lg font-bold text-green-400">{data.healthy_days}</p>
                  <p className="text-[10px] text-dark-400">Healthy</p>
                </div>
                <div className="text-center bg-dark-800 rounded-lg p-2">
                  <p className="text-lg font-bold text-yellow-400">{data.warning_days}</p>
                  <p className="text-[10px] text-dark-400">Warning</p>
                </div>
                <div className={`text-center rounded-lg p-2 ${data.missing_days > 0 ? 'bg-red-900/40 ring-1 ring-red-500' : 'bg-dark-800'}`}>
                  <p className={`text-lg font-bold ${data.missing_days > 0 ? 'text-red-300' : 'text-red-600'}`}>
                    {data.missing_days}
                  </p>
                  <p className="text-[10px] text-dark-400">Missing</p>
                </div>
                <div className="text-center bg-dark-800 rounded-lg p-2">
                  <p className="text-lg font-bold text-blue-400">{data.holiday_days || 0}</p>
                  <p className="text-[10px] text-dark-400">Holidays</p>
                </div>
              </div>

              {/* Close Mismatch Row (only if any) */}
              {hasCloseMismatch && (
                <div className="mb-4 px-3 py-2 bg-orange-900/20 border border-orange-700/30 rounded-lg">
                  <span className="text-orange-400 text-xs font-medium">
                    {data.close_mismatch_days} day{data.close_mismatch_days > 1 ? 's' : ''} with close price mismatch
                  </span>
                </div>
              )}

              {/* Daily Timeline */}
              <div className="bg-dark-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-white">Daily Status</p>
                  <span className="text-xs text-dark-400">{selectedPeriod}d</span>
                </div>
                <div className="flex justify-center mb-3">
                  <div className="flex gap-0.5 flex-wrap max-w-full">
                    {data.daily_status?.slice(-selectedPeriod).map((day, index) => {
                      const date = new Date(day.date + 'T00:00:00');
                      const humanDate = date.toLocaleDateString('en-US', {
                        weekday: 'short', month: 'short', day: 'numeric'
                      });

                      // Build structured tooltip content
                      const buildTooltip = () => {
                        const statusLabels = {
                          healthy: 'Healthy',
                          warning: 'Warning',
                          critical: 'Critical',
                          missing: 'Missing Data',
                          close_mismatch: 'Close Mismatch',
                          holiday: 'Holiday / Weekend',
                          pending: 'Pending',
                        };
                        const statusEmoji = {
                          healthy: '\u2705', warning: '\u26A0\uFE0F', critical: '\u274C',
                          missing: '\u{1F6A8}', close_mismatch: '\u{1F7E0}',
                          holiday: '\u{1F4C5}', pending: '\u23F3',
                        };

                        let lines = [];
                        lines.push(`${humanDate}`);
                        lines.push(`${statusEmoji[day.status] || ''} ${statusLabels[day.status] || day.status}`);

                        if (day.issues && day.issues.length > 0) {
                          // Separate close mismatch from missing columns
                          const mismatch = day.issues.filter(i => i.startsWith('close_mismatch'));
                          const columns = day.issues.filter(i => !i.startsWith('close_mismatch'));

                          if (mismatch.length > 0) {
                            // Parse "close_mismatch (DB:12345.67, EODHD:12346)" into readable format
                            const m = mismatch[0];
                            const match = m.match(/DB:([\d.]+).*EODHD:([\d.]+)/);
                            if (match) {
                              const dbVal = parseFloat(match[1]).toLocaleString(undefined, { maximumFractionDigits: 2 });
                              const eodVal = parseFloat(match[2]).toLocaleString(undefined, { maximumFractionDigits: 2 });
                              lines.push(`DB Close: ${dbVal}`);
                              lines.push(`EODHD Close: ${eodVal}`);
                            }
                          }

                          if (columns.length > 0 && columns.length <= 4) {
                            lines.push(`Missing: ${columns.join(', ')}`);
                          } else if (columns.length > 4) {
                            lines.push(`Missing: ${columns.length} columns`);
                            lines.push(columns.slice(0, 4).join(', ') + '...');
                          }
                        }

                        return lines;
                      };

                      const tooltipLines = buildTooltip();

                      return (
                        <div
                          key={index}
                          className={`w-3.5 h-7 rounded-sm flex-shrink-0 ${getStatusColor(day.status)} cursor-pointer hover:opacity-80 transition-opacity`}
                          onMouseEnter={(e) => {
                            const rect = e.target.getBoundingClientRect();
                            setTooltip({
                              show: true,
                              content: tooltipLines,
                              x: rect.left + rect.width / 2,
                              y: rect.top - 10
                            });
                          }}
                          onMouseLeave={() => setTooltip({ show: false, content: null, x: 0, y: 0 })}
                          style={{ minWidth: '14px', minHeight: '28px' }}
                        ></div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-dark-400">
                  <span>Oldest</span>
                  <span>Latest</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Custom Tooltip */}
      {tooltip.show && tooltip.content && (
        <div
          className="fixed z-50 bg-dark-900 border border-dark-600 rounded-lg px-3.5 py-2.5 shadow-xl pointer-events-none max-w-xs"
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: 'translateX(-50%) translateY(-100%)',
          }}
        >
          {Array.isArray(tooltip.content) ? (
            <div className="space-y-0.5">
              {tooltip.content.map((line, i) => (
                <div key={i} className={
                  i === 0 ? 'text-xs font-semibold text-white' :
                  i === 1 ? 'text-xs font-medium text-dark-200' :
                  'text-[11px] text-dark-400'
                }>{line}</div>
              ))}
            </div>
          ) : (
            <div className="whitespace-pre-line text-sm text-white">{tooltip.content}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default StockHealthMonitor;
